"use client"

import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import { convex, whenConvexReady } from '@/lib/convex-client'
import { api } from "@/convex/_generated/api"
import { useAuthStore } from './auth-store'
import { featureFlagService } from '@/lib/feature-flags'

export type UserRole = "basic" | "essential" | "pro"

export interface RoleLimits {
  // Brain Dumping
  maxTasksPerMonth: number

  // Pomodoro Timer
  maxTimerTasksPerDay: number

  // Time Statistics & History
  maxStatsDays: number
  maxHistoryDays: number
}

// Keep legacy ROLE_LIMITS for backward compatibility
export const ROLE_LIMITS: Record<UserRole, RoleLimits> = {
  basic: {
    maxTasksPerMonth: Number.POSITIVE_INFINITY,
    maxTimerTasksPerDay: 1,
    maxStatsDays: 7,
    maxHistoryDays: 7,
  },
  essential: {
    maxTasksPerMonth: Number.POSITIVE_INFINITY,
    maxTimerTasksPerDay: Number.POSITIVE_INFINITY,
    maxStatsDays: Number.POSITIVE_INFINITY,
    maxHistoryDays: Number.POSITIVE_INFINITY,
  },
  pro: {
    maxTasksPerMonth: Number.POSITIVE_INFINITY,
    maxTimerTasksPerDay: Number.POSITIVE_INFINITY,
    maxStatsDays: Number.POSITIVE_INFINITY,
    maxHistoryDays: Number.POSITIVE_INFINITY,
  },
}

interface EnhancedUserRoleState {
  // Legacy state (for backward compatibility)
  userRole: UserRole
  roleLimits: RoleLimits
  isLoading: boolean
  tasksCreatedThisMonth: number
  timerTasksToday: number
  
  // Enhanced feature flag state
  featureFlags: Map<string, boolean>
  dynamicLimits: Map<string, number>
  lastFeatureFlagUpdate: number
  
  // Legacy actions
  refreshUsage: () => Promise<void>
  fetchUserRoleAndUsage: () => Promise<void>
  
  // Enhanced feature flag actions
  isFeatureEnabled: (flagKey: string) => Promise<boolean>
  canUseFeature: (featureKey: string, limitType?: 'daily' | 'monthly' | 'total') => Promise<{
    allowed: boolean
    limit: number
    current_usage: number
    remaining: number
    user_role: string
  }>
  useFeature: (featureKey: string, limitType?: 'daily' | 'monthly' | 'total') => Promise<{
    allowed: boolean
    limit: number
    current_usage: number
    remaining: number
    user_role: string
  }>
  getDynamicLimit: (featureKey: string, limitType?: 'daily' | 'monthly' | 'total') => Promise<number>
  refreshFeatureFlags: () => Promise<void>
  
  // Team Collaboration Methods
  canCreateProject: () => boolean
  canUseTeamCollaboration: () => boolean
  getTeamMemberLimit: () => number
  canInviteTeamMembers: () => boolean

  // Internal setters
  setUserRole: (role: UserRole) => void
  setIsLoading: (loading: boolean) => void
  setTasksCreatedThisMonth: (count: number) => void
  setTimerTasksToday: (count: number) => void
}

export const useEnhancedUserRoleStore = create<EnhancedUserRoleState>()(
  devtools(
    (set, get) => ({
      // Initial state
      userRole: "basic",
      roleLimits: ROLE_LIMITS.basic,
      isLoading: true,
      tasksCreatedThisMonth: 0,
      timerTasksToday: 0,
      featureFlags: new Map(),
      dynamicLimits: new Map(),
      lastFeatureFlagUpdate: 0,

      // Internal setters
      setUserRole: (userRole) => set({ userRole, roleLimits: ROLE_LIMITS[userRole] }),
      setIsLoading: (isLoading) => set({ isLoading }),
      setTasksCreatedThisMonth: (tasksCreatedThisMonth) => set({ tasksCreatedThisMonth }),
      setTimerTasksToday: (timerTasksToday) => set({ timerTasksToday }),

      // Enhanced feature flag actions
      isFeatureEnabled: async (flagKey: string) => {
        try {
          return await featureFlagService.isFeatureEnabled(flagKey)
        } catch (error) {
          console.error(`Error checking feature flag ${flagKey}:`, error)
          return false
        }
      },

      canUseFeature: async (featureKey: string, limitType = 'daily') => {
        try {
          return await featureFlagService.checkUserLimit(featureKey, limitType)
        } catch (error) {
          console.error(`Error checking feature limit ${featureKey}:`, error)
          return {
            allowed: false,
            limit: 0,
            current_usage: 0,
            remaining: 0,
            user_role: get().userRole
          }
        }
      },

      useFeature: async (featureKey: string, limitType = 'daily') => {
        try {
          const result = await featureFlagService.checkAndRecordUsage(featureKey, limitType)
          
          // Update local state if this is a known feature
          if (featureKey === 'timer_tasks' && limitType === 'daily') {
            set({ timerTasksToday: result.current_usage })
          }
          
          return result
        } catch (error) {
          console.error(`Error using feature ${featureKey}:`, error)
          return {
            allowed: false,
            limit: 0,
            current_usage: 0,
            remaining: 0,
            user_role: get().userRole
          }
        }
      },

      getDynamicLimit: async (featureKey: string, limitType = 'daily') => {
        try {
          return await featureFlagService.getRoleLimit(get().userRole, featureKey, limitType)
        } catch (error) {
          console.error(`Error getting dynamic limit for ${featureKey}:`, error)
          return 0
        }
      },

      refreshFeatureFlags: async () => {
        try {
          // Clear cache to force refresh
          featureFlagService.clearCache()
          
          // Fetch fresh data
          await featureFlagService.fetchFeatureData()
          
          set({ lastFeatureFlagUpdate: Date.now() })
        } catch (error) {
          console.error('Error refreshing feature flags:', error)
        }
      },

      // Team Collaboration Methods
      canCreateProject: () => get().userRole === 'pro',
      canUseTeamCollaboration: () => get().userRole === 'pro',
      getTeamMemberLimit: () => {
        const role = get().userRole
        return role === 'pro' ? 10 : 0
      },
      canInviteTeamMembers: () => get().userRole === 'pro',

      // Legacy methods (for backward compatibility)
      fetchUserRoleAndUsage: async () => {
        const user = useAuthStore.getState().user
        if (!user) {
          set({ isLoading: false })
          return
        }

        try {
          set({ isLoading: true })

          // Role and usage come from Convex in one authenticated query. Period
          // boundaries are the user's local calendar, so they are computed here.
          const now = new Date()
          const monthStart = new Date(now.getFullYear(), now.getMonth(), 1)
          const monthEnd = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59, 999)
          const dayStart = new Date(now)
          dayStart.setHours(0, 0, 0, 0)
          const dayEnd = new Date(dayStart)
          dayEnd.setDate(dayEnd.getDate() + 1)

          await whenConvexReady()

          const usage = await convex.query(api.limits.currentUsage, {
            monthStart: monthStart.getTime(),
            monthEnd: monthEnd.getTime(),
            dayStart: dayStart.getTime(),
            dayEnd: dayEnd.getTime(),
          })

          if (usage.role === "basic" || usage.role === "essential" || usage.role === "pro") {
            get().setUserRole(usage.role as UserRole)
          }

          set({
            tasksCreatedThisMonth: usage.tasksCreatedThisMonth,
            timerTasksToday: usage.timerTasksToday,
          })
        } catch (error) {
          console.error("Error in fetchUserRoleAndUsage:", error)
        } finally {
          set({ isLoading: false })
        }
      },

      refreshUsage: async () => {
        await get().fetchUserRoleAndUsage()
      },


    }),
    { name: 'enhanced-user-role-store' }
  )
)
